import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { PageHeader } from "@/components/Bits";
import OutlookConnect from "@/components/OutlookConnect";

const PROVIDERS = [["gemini", "Google AI / Gemini"], ["openai", "OpenAI / ChatGPT"], ["anthropic", "Anthropic / Claude"]];
const TASK_LABELS = { research_enhancement: "Research enhancement", lead_summaries: "Lead summaries", website_issue_summaries: "Website issue summaries",
  draft_improvement: "Draft improvement", subject_suggestions: "Subject line suggestions", prioritization: "Prioritization suggestions" };

function Card({ title, hint, children, testid }) {
  return (
    <div className="kh-card space-y-4 p-6" data-testid={testid}>
      <div><h3 className="font-display text-lg font-medium">{title}</h3>{hint && <p className="mt-1 text-sm text-muted-foreground">{hint}</p>}</div>
      {children}
    </div>
  );
}

function KeyInput({ provider, isSet, onSaved }) {
  const [v, setV] = useState("");
  const save = async (val) => {
    try { const { data } = await api.put("/settings/keys", { provider, key: val }); onSaved(data); setV(""); toast.success(val ? "Key saved (encrypted)" : "Key removed"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="flex gap-2">
      <Input type="password" placeholder={isSet ? "•••••••••• saved" : "Paste API key"} value={v} onChange={(e) => setV(e.target.value)} data-testid={`key-input-${provider}`} />
      <Button variant="secondary" disabled={!v} onClick={() => save(v)} data-testid={`key-save-${provider}`}>Save</Button>
      {isSet && <Button variant="ghost" onClick={() => save("")} data-testid={`key-remove-${provider}`}>Remove</Button>}
    </div>
  );
}

function DataCard() {
  const file = useRef();
  const exportData = async () => {
    const { data } = await api.get("/data/export");
    const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: "application/json" }));
    Object.assign(document.createElement("a"), { href: url, download: `khanata-export-${new Date().toISOString().slice(0, 10)}.json` }).click();
  };
  const importData = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    try { const { data } = await api.post("/data/import", JSON.parse(await f.text())); toast.success(`Imported: ${Object.entries(data.imported).map(([k, v]) => `${v} ${k}`).join(", ")}`); }
    catch (x) { toast.error(errMsg(x)); }
  };
  return (
    <Card title="Data export / import" hint="Move tracks, leads, drafts, suppression and history to another server (API keys are not exported)." testid="data-card">
      <div className="flex gap-2">
        <Button variant="secondary" onClick={exportData} data-testid="export-data-btn">Export JSON</Button>
        <Button variant="outline" onClick={() => file.current.click()} data-testid="import-data-btn">Import JSON</Button>
        <input ref={file} type="file" accept="application/json" hidden onChange={importData} />
      </div>
    </Card>
  );
}

function PasswordCard() {
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const save = async () => { try { await api.post("/auth/change-password", { current_password: cur, new_password: nw }); toast.success("Password changed"); setCur(""); setNw(""); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Card title="Your password" testid="password-card">
      <div className="flex max-w-xl gap-2">
        <Input type="password" placeholder="Current" value={cur} onChange={(e) => setCur(e.target.value)} data-testid="current-password-input" />
        <Input type="password" placeholder="New (8+ chars)" value={nw} onChange={(e) => setNw(e.target.value)} data-testid="new-password-input" />
        <Button onClick={save} disabled={!cur || nw.length < 8} data-testid="change-password-btn">Update</Button>
      </div>
    </Card>
  );
}

export default function Settings() {
  const [s, setS] = useState(null);
  const [params] = useSearchParams();
  useEffect(() => { api.get("/settings").then((r) => setS(r.data)); }, []);
  if (!s) return <div className="text-muted-foreground">Loading…</div>;
  const set = (p) => setS((x) => ({ ...x, ...p }));
  const setAi = (p) => setS((x) => ({ ...x, ai: { ...x.ai, ...p } }));
  const save = async () => {
    try {
      const { data } = await api.put("/settings", { daily_cap: s.daily_cap, sender_name: s.sender_name, ai_enabled: s.ai.enabled, ai_provider: s.ai.provider, ai_model: s.ai.model, ai_tasks: s.ai.tasks });
      setS(data); toast.success("Settings saved"); window.dispatchEvent(new Event("kh-quota"));
    } catch (e) { toast.error(errMsg(e)); }
  };
  const test = async () => { try { const { data } = await api.post("/settings/ai/test"); toast.success(`Provider replied: ${data.reply}`); } catch (e) { toast.error(errMsg(e)); } };

  return (
    <>
      <PageHeader eyebrow="Workspace" title="Settings"><Button onClick={save} data-testid="settings-save-btn">Save settings</Button></PageHeader>
      <Tabs defaultValue={params.get("outlook") ? "outlook" : "general"}>
        <TabsList className="h-auto flex-wrap bg-secondary/60">
          {[["general", "General"], ["outlook", "Outlook"], ["ai", "AI Integrations"], ["sources", "Sources"], ["data", "Data & account"]].map(([v, l]) => <TabsTrigger key={v} value={v} data-testid={`settings-tab-${v}`}>{l}</TabsTrigger>)}
        </TabsList>
        <TabsContent value="general" className="mt-6 space-y-4">
          <Card title="Daily outreach batch" hint="Default 5 per day, hard maximum 10. Extra qualified leads stay in the queue." testid="daily-cap-card">
            <div className="flex max-w-md items-center gap-4">
              <Slider value={[s.daily_cap]} min={1} max={10} step={1} onValueChange={([v]) => set({ daily_cap: v })} data-testid="daily-cap-slider" />
              <span className="w-20 font-mono" data-testid="daily-cap-value">{s.daily_cap}/day</span>
            </div>
          </Card>
          <Card title="Sender name" hint="Used as {sender_name} in templates. Leave blank to use the signed-in user's name.">
            <Input className="max-w-sm" value={s.sender_name} onChange={(e) => set({ sender_name: e.target.value })} data-testid="sender-name-input" />
          </Card>
        </TabsContent>
        <TabsContent value="outlook" className="mt-6"><OutlookConnect /></TabsContent>
        <TabsContent value="ai" className="mt-6 space-y-4">
          <Card title="AI enhancement" hint="Optional. The app works fully without AI; if a key is missing or a provider fails, everything falls back to rules and templates. AI never verifies contacts, dedupes, approves sends or decides suppression." testid="ai-card">
            <div className="flex items-center gap-3"><Switch checked={s.ai.enabled} onCheckedChange={(v) => setAi({ enabled: v })} data-testid="ai-enabled-switch" /><span className="text-sm">{s.ai.enabled ? "AI enabled" : "AI disabled (non-AI core mode)"}</span></div>
            <div className="grid max-w-2xl gap-3 sm:grid-cols-2">
              <Select value={s.ai.provider || ""} onValueChange={(v) => setAi({ provider: v })}>
                <SelectTrigger data-testid="ai-provider-select"><SelectValue placeholder="Choose provider" /></SelectTrigger>
                <SelectContent>{PROVIDERS.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`ai-provider-${v}`}>{l}</SelectItem>)}</SelectContent>
              </Select>
              <Input placeholder={`Model (default ${s.ai.default_models[s.ai.provider] || "—"})`} value={s.ai.model} onChange={(e) => setAi({ model: e.target.value })} data-testid="ai-model-input" />
            </div>
            <div className="space-y-3">
              {PROVIDERS.map(([v, l]) => (
                <div key={v} className="grid max-w-2xl items-center gap-2 sm:grid-cols-[170px_1fr]">
                  <span className="text-sm">{l} {s.ai.keys_set[v] && <span className="text-xs text-emerald-700">· saved</span>}</span>
                  <KeyInput provider={v} isSet={s.ai.keys_set[v]} onSaved={(d) => setS((x) => ({ ...x, ai: { ...x.ai, keys_set: d.ai.keys_set } }))} />
                </div>
              ))}
            </div>
            <div className="grid max-w-2xl gap-2 sm:grid-cols-2">
              {Object.entries(TASK_LABELS).map(([k, l]) => (
                <div key={k} className="flex items-center gap-3 rounded-lg border px-3 py-2">
                  <Switch checked={!!s.ai.tasks[k]} onCheckedChange={(v) => setAi({ tasks: { ...s.ai.tasks, [k]: v } })} data-testid={`ai-task-${k}`} />
                  <span className="text-sm">{l}</span>
                </div>
              ))}
            </div>
            <div className="flex gap-2"><Button variant="secondary" onClick={save}>Save AI settings</Button><Button variant="outline" onClick={test} data-testid="ai-test-btn">Test connection</Button></div>
          </Card>
        </TabsContent>
        <TabsContent value="sources" className="mt-6">
          <Card title="YouTube Data API" hint="Optional. Enables channel discovery for the Motion Graphics › YouTube niche. Without it, paste channel/website seed URLs." testid="youtube-card">
            <div className="max-w-xl"><KeyInput provider="youtube" isSet={s.source_keys_set.youtube} onSaved={(d) => set({ source_keys_set: d.source_keys_set })} /></div>
            <p className="text-xs text-muted-foreground">Directory search (OpenStreetMap) and website audits need no key.</p>
          </Card>
        </TabsContent>
        <TabsContent value="data" className="mt-6 space-y-4"><DataCard /><PasswordCard /></TabsContent>
      </Tabs>
    </>
  );
}
